/**
 * Listener Action Handlers
 * 
 * Callbacks executed when listener data changes.
 * Each handler receives: (data, context) => void
 * 
 * Context contains: { user, navigate, stores, queryClient, notification, alert }
 */

import { 
  GLOBAL_ACTIONS, 
  CHAT_METADATA_HANDLER_KEYS,
  SPECTRA_HANDLER_KEYS,
  CHAT_ACTIONS 
} from './constants';
import QUERYKEYS from '../../api/queryKeys';
import logger from '../Logger';
import useStreamingStateStore from '../../store/useStreamingStateStore';
import useGenerateApiSignal from '../../store/useGenerateApiSignal';
import useArtifactEditSignal from '../../store/useArtifactEditSignal';
import { isTerminalAnswerStatus } from '@photon/chat-core';

// ========================================
// GLOBAL ACTION HANDLERS
// ========================================

export const handleAddCollaborator = (data, context) => {
  const { notification, queryClient, params } = context;
  
  notification.addNotification({
    category: 'collaboration',
    message: data?.message,
    navigateTo: `/spectra/${data?.data?.space_id}`,
  });
  
  // React Query handles data updates via invalidation
  if (params.space_id === data?.data?.space_id) {
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS, params.space_id] });
  }
  
  // Invalidate spectras list to update sidebar
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
};

export const handleRemoveCollaborator = (data, context) => {
  const { user, notification, queryClient, navigate, alert, params } = context;
  
  notification.addNotification({
    category: 'collaboration',
    message: data?.message,
    navigateTo: `/spectra/${data?.data?.space_id}`,
  });
  
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS, params.space_id] });
  
  // Invalidate spectras list to update sidebar
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
  
  if (
    data?.spectra_id &&
    user.uid === data?.data?.user_id &&
    params.space_id === data.spectra_id
  ) {
    alert.setIsOpen(
      true,
      'Removed Collaboration',
      'You have been removed from this spectra!'
    );
    navigate('/spectra');
  }
};

export const handleEditSpace = (data, context) => {
  const { notification, queryClient, params } = context;
  
  notification.addNotification({
    category: 'space',
    message: data?.message,
    navigateTo: `/spectra/${data?.data?.space_id}`,
  });
  
  // React Query handles data updates via invalidation
  if (params.space_id === data?.data?.space_id) {
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRA_DETAILS, params.space_id] });
  }
  
  // Invalidate spectras list to update sidebar
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
};

export const handleDeleteSpace = (data, context) => {
  const { notification, navigate, alert, params, queryClient } = context;
  
  notification.addNotification({
    category: 'space',
    message: data?.message,
    navigateTo: '/spectra',
  });
  
  // Invalidate spectras list to update sidebar
  if (queryClient) {
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
  }
  
  if (params.space_id === data?.data?.space_id) {
    alert.setIsOpen(
      true,
      'Spectra Deleted',
      'This Spectra is deleted by the owner!'
    );
    navigate('/spectra');
  }
};

export const handleLeaveSpace = (data, context) => {
  const { notification, navigate, params, queryClient } = context;
  
  // If it's not current user's session, add notification
  if (data?.session_id !== context.sessionId) {
    notification.addNotification({
      category: 'collaboration',
      message: data?.message,
      navigateTo: `/spectra/${data?.spectra_id}`,
    });
  } else {
    // Current user is leaving - navigate away and invalidate
    if (data?.spectra_id) {
      queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
      if (params.space_id === data.spectra_id) {
        navigate('/spectra');
      }
    }
  }
};

// ========================================
// HANDLER REGISTRY
// ========================================

export const handleSpaceAccessRevoked = (data, context) => {
  const { queryClient, navigate, listenerManager } = context;

  const spaceId = data?.data?.space_id;

  if (spaceId && listenerManager) {
    const contextSpace = context.space;
    const space = (contextSpace?.space_id === spaceId)
      ? contextSpace
      : { space_id: spaceId, owner_id: data?.data?.owner_id };
    listenerManager.unsubscribeSpectra(space);
  }

  if (queryClient) {
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.SPECTRAS] });
  }

  navigate('/spectra');
};

// ========================================
// DESKTOP BRIDGE — DEVICE PRESENCE (Phase G5)
// ========================================
// Handles DEVICE_CONNECTED / DEVICE_DISCONNECTED events pushed via SSE from
// photon-ws → photon-api → SSE stream. Updates the live device cache instantly
// without a network round-trip so the Prompt-bar device chip reacts in real time.

export const handleDeviceConnected = (data, context) => {
  const { queryClient } = context;
  if (!queryClient || !data?.device_id) return;
  queryClient.setQueryData(["desktop-user-devices"], (old) => {
    const entry = {
      device_id: data.device_id,
      device_name: data.device_name || "",
      client_id: data.client_id || "",
      connected_at: data.connected_at ?? null,
    };
    if (!old) return [entry];
    // Reconnecting device: remove stale entry first, then prepend the fresh one.
    const filtered = old.filter((d) => d.device_id !== data.device_id);
    return [entry, ...filtered];
  });
};

export const handleDeviceDisconnected = (data, context) => {
  const { queryClient } = context;
  if (!queryClient || !data?.device_id) return;
  queryClient.setQueryData(["desktop-user-devices"], (old) => {
    if (!old) return old;
    return old.filter((d) => d.device_id !== data.device_id);
  });
};

export const globalHandlers = {
  [GLOBAL_ACTIONS.ADD_COLLABORATOR]: handleAddCollaborator,
  [GLOBAL_ACTIONS.REMOVE_COLLABORATOR]: handleRemoveCollaborator,
  [GLOBAL_ACTIONS.EDIT_SPACE]: handleEditSpace,
  [GLOBAL_ACTIONS.DELETE_SPACE]: handleDeleteSpace,
  [GLOBAL_ACTIONS.LEAVE_SPACE]: handleLeaveSpace,
  [GLOBAL_ACTIONS.SPACE_ACCESS_REVOKED]: handleSpaceAccessRevoked,
  [GLOBAL_ACTIONS.NOTIFICATION_STATE_CHANGED]: (data, context) => {
    // Another tab performed read-all or clear-all — re-hydrate from backend.
    const { user, orgId, notification } = context;
    if (!user?.uid || !orgId || !notification?.hydrate) return;
    import('../../api/notifications').then(({ getNotifications }) => {
      getNotifications(orgId, user.uid)
        .then((records) => notification.hydrate(records))
        .catch(() => {});
    });
  },
  // Phase G5 — Desktop Bridge device presence events
  [GLOBAL_ACTIONS.DEVICE_CONNECTED]: handleDeviceConnected,
  [GLOBAL_ACTIONS.DEVICE_DISCONNECTED]: handleDeviceDisconnected,
};

// ========================================
// HANDLER UTILITIES
// ========================================

export const isFreshAction = (data) => {
  const timestamp = data?.timestamp || data?.updated_at || data?.created_at;
  if (!timestamp) return false;

  const actionTime = timestamp?.toDate?.() || new Date(timestamp);
  return (new Date() - actionTime) / 1000 <= 5;
};

export const shouldProcessAction = (data, currentSessionId, currentUserId) => {
  // Skip if action was performed by current user (across any session)
  if (data?.by && currentUserId && data.by === currentUserId) {
    return false;
  }
  
  // Skip if action originated from current session (same tab)
  if (data?.session_id && currentSessionId && data.session_id === currentSessionId) {
    return false;
  }

  // Skip stale actions (older than 5 seconds) - these are from initial snapshot, not live updates
  // Also skip if no timestamp exists (legacy data)
  return isFreshAction(data);
};

/**
 * Guard used by every Spectra chat-list handler (add/edit/delete/move/pin).
 * Logs and returns false if the space context is missing so the caller can
 * bail out early — extracted so the identical check/warn/return can't drift
 * across handlers.
 */
export const requireSpaceContext = (space, handlerName) => {
  if (!space?.space_id) {
    logger.warn('ListenerManager', `${handlerName} called without space context`);
    return false;
  }
  return true;
};

/**
 * Shared query key for the Spectra chat-list ("accordion") query, used by
 * every Spectra chat-list handler. Centralised so the key shape can't drift
 * between call sites (a stray typo here silently breaks cache invalidation
 * for just that one handler).
 */
export const getSpectraSparksQueryKey = (space, orgId, userId) => (
  ['spectra-sparks', space.space_id, orgId, userId, space.owner_id]
);

/**
 * Shared CHAT_MESSAGES invalidation used by the generation-terminal handlers
 * and handleArtifactUpdated (all three-part-key call sites, i.e. those that
 * scope the key by space_id). handleAddMessage/handleRegenerateMessage
 * intentionally use a shorter chatId-only key (React Query's prefix-based
 * invalidation matching still catches the space-scoped queries), so they are
 * NOT routed through this helper.
 */
export const invalidateChatMessages = (queryClient, chatId, space) => {
  queryClient.invalidateQueries({
    queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id],
  });
};

/**
 * Shared streaming-state guard for handleAddMessage/handleRegenerateMessage:
 * skip a cache refetch while the message is actively streaming, or while a
 * completed snapshot is still propagating to Firestore. Both handlers must
 * key this off prompt_id, not answer_id — see 2026-07-01 bug fix.
 */
export const isStreamingOrSnapshotted = (messageId) => {
  if (!messageId) return false;
  const store = useStreamingStateStore.getState();
  return store.isStreaming(messageId) || store.isCompletedSnapshot(messageId);
};

// ========================================
// CHAT METADATA ACTION HANDLERS
// ========================================
// Handles standalone spark metadata events
// Always-on listeners (active from login to logout)

export const handleChatMetadataPin = (data, context) => {
  const { notification, queryClient, orgId, user } = context;
  const userId = user?.uid;
  
  notification.addNotification({
    category: 'chat',
    message: data?.message,
    navigateTo: `/chat/${data?.data?.spark_id}`,
  });
  
  if (data?.data?.spark_id !== undefined && data?.data?.pin_value !== undefined && queryClient) {
    // Invalidate chats list to update pin status in sidebar
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHATS, orgId, userId] });
  }
};

export const handleChatMetadataEdit = (data, context) => {
  const { notification, queryClient, orgId, user } = context;
  const userId = user?.uid;
  
  if (data?.emitAgain) {
    notification.addNotification({
      category: 'chat',
      message: data?.message,
      navigateTo: `/chat/${data?.data?.uid}`,
    });
  }
  
  if (data?.data?.uid && data?.data?.title !== undefined && queryClient) {
    const queryKey = [QUERYKEYS.CHATS, orgId, userId];
    // Patch cache with server-confirmed title from SSE payload — no refetch needed
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;
      if (Array.isArray(oldData)) {
        return oldData.map(c =>
          c.uid === data.data.uid ? { ...c, title: data.data.title } : c
        );
      }
      return oldData;
    });
  }
  // Note: Dashboard refetch is handled in ListenerManager.handleData() before user filter
};

export const handleChatMetadataDelete = (data, context) => {
  const { notification, queryClient, navigate, alert, params, orgId, user } = context;
  const userId = user?.uid;
  
  notification.addNotification({
    category: 'chat',
    message: data?.message,
    navigateTo: '/spark',
  });
  
  // Backend may send chat_id directly or in data object
  const deletedChatId = data?.chat_id || data?.data?.spark_id;
  
  if (deletedChatId) {
    // Optimistically remove from cache for instant UI update
    if (queryClient) {
      const queryKey = [QUERYKEYS.CHATS, orgId, userId];
      queryClient.setQueryData(queryKey, (oldData) => {
        if (!oldData) return oldData;
        
        if (Array.isArray(oldData)) {
          return oldData.filter(c => c.uid !== deletedChatId);
        }
        
        return oldData;
      });
    }
    
    // Navigate away if currently viewing this chat
    if (params.id === deletedChatId) {
      alert.setIsOpen(true, 'Spark Removed', 'This spark is removed!');
      navigate('/chat');
    }
  }
  // Note: Dashboard refetch is handled in ListenerManager.handleData() before user filter
};

// ========================================
// CHAT METADATA HANDLER REGISTRY
// ========================================
// Uses unique handler keys to avoid collision with spectra handlers

export const chatMetadataHandlers = {
  [CHAT_METADATA_HANDLER_KEYS.PIN_UNPIN_CHAT]: handleChatMetadataPin,
  [CHAT_METADATA_HANDLER_KEYS.EDIT_CHAT]: handleChatMetadataEdit,
  [CHAT_METADATA_HANDLER_KEYS.DELETE_CHAT]: handleChatMetadataDelete,
};

// ========================================
// SPECTRA ACTION HANDLERS
// ========================================

export const handleAddChat = (data, context) => {
  const { notification, queryClient, space, orgId, user } = context;
  const userId = user?.uid;
  
  if (!requireSpaceContext(space, 'handleAddChat')) return;
  
  notification.addNotification({
    category: 'space',
    message: data?.message,
    navigateTo: `/spectra/${space.space_id}/chat`,
  });
  
  if (queryClient) {
    // Invalidate spectra-sparks query to update chat list in accordion
    queryClient.invalidateQueries({ queryKey: getSpectraSparksQueryKey(space, orgId, userId) });
  }
};

export const handleEditChat = (data, context) => {
  const { notification, queryClient, space, orgId, user } = context;
  const userId = user?.uid;
  
  if (!requireSpaceContext(space, 'handleEditChat')) return;
  
  if (data?.emitAgain) {
    notification.addNotification({
      category: 'space',
      message: data?.message,
      navigateTo: `/spectra/${space.space_id}/chat/${data?.data?.uid}`,
    });
  }
  
  if (queryClient && data?.data?.uid && data?.data?.title !== undefined) {
    // Patch cache with server-confirmed title from SSE payload — no refetch needed
    const queryKey = getSpectraSparksQueryKey(space, orgId, userId);
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;
      if (oldData.sparks && Array.isArray(oldData.sparks)) {
        return {
          ...oldData,
          sparks: oldData.sparks.map(s =>
            s.uid === data.data.uid ? { ...s, title: data.data.title } : s
          ),
        };
      }
      return oldData;
    });
  }
};

export const handleDeleteChat = (data, context) => {
  const { notification, queryClient, navigate, alert, params, space, orgId, user } = context;
  const userId = user?.uid;
  
  if (!requireSpaceContext(space, 'handleDeleteChat')) return;
  
  notification.addNotification({
    category: 'space',
    message: data?.message,
    navigateTo: `/spectra/${space.space_id}`,
  });
  
  const deletedSparkId = data.data?.spark_id || data.data?.chat_id;
  
  // Optimistically remove from cache for instant UI update
  if (deletedSparkId && queryClient) {
    const queryKey = getSpectraSparksQueryKey(space, orgId, userId);
    queryClient.setQueryData(queryKey, (oldData) => {
      if (!oldData) return oldData;
      
      if (oldData.sparks && Array.isArray(oldData.sparks)) {
        return {
          ...oldData,
          sparks: oldData.sparks.filter(s => s.uid !== deletedSparkId)
        };
      }
      
      return oldData;
    });
  }
  
  // Navigate away if currently viewing the deleted spark
  if (params.id === deletedSparkId) {
    alert.setIsOpen(true, 'Spark Removed', 'This spark is removed!');
    navigate(`/spectra/${space.space_id}/chat`);
  }
};

export const handleMoveChat = (data, context) => {
  const { notification, queryClient, navigate, alert, params, space, orgId, user } = context;
  const userId = user?.uid;
  
  if (!requireSpaceContext(space, 'handleMoveChat')) return;
  
  notification.addNotification({
    category: 'space',
    message: data?.message,
    navigateTo: `/spectra/${space.space_id}`,
  });
  
  if (queryClient) {
    const queryKey = getSpectraSparksQueryKey(space, orgId, userId);
    if (data.to_space === space.space_id) {
      // Spark moved TO this spectra - invalidate to add it
      queryClient.invalidateQueries({ queryKey });
    } else {
      // Spark moved FROM this spectra - invalidate to remove it
      if (data?.chat_data) {
        data.chat_data.forEach((chat) => {
          if (params.id === chat.uid) {
            alert.setIsOpen(
              true,
              'Spark Moved',
              'This spark has been moved by owner!'
            );
            navigate(`/spectra/${data.from_space}/chat`);
          }
        });
      }
      queryClient.invalidateQueries({ queryKey });
    }
  }
};

export const handlePinUnpinChat = (data, context) => {
  const { notification, queryClient, space, orgId, user } = context;
  const userId = user?.uid;
  
  if (!requireSpaceContext(space, 'handlePinUnpinChat')) return;
  
  notification.addNotification({
    category: 'space',
    message: data?.message,
    navigateTo: `/spectra/${space.space_id}`,
  });
  
  if (queryClient) {
    // Invalidate spectra-sparks query to update pin status in accordion
    queryClient.invalidateQueries({ queryKey: getSpectraSparksQueryKey(space, orgId, userId) });
  }
};

export const handleDeleteSpark = (data, context) => {
  // DELETE_SPARK is permanent removal - similar to DELETE_CHAT
  handleDeleteChat(data, context);
};

// ========================================
// SPECTRA HANDLER REGISTRY
// ========================================
// Uses unique handler keys to avoid collision with chat metadata handlers

export const spectraHandlers = {
  [SPECTRA_HANDLER_KEYS.ADD_CHAT]: handleAddChat,
  [SPECTRA_HANDLER_KEYS.EDIT_CHAT]: handleEditChat,
  [SPECTRA_HANDLER_KEYS.DELETE_CHAT]: handleDeleteChat,
  [SPECTRA_HANDLER_KEYS.MOVE_CHAT]: handleMoveChat,
  [SPECTRA_HANDLER_KEYS.PIN_UNPIN_CHAT]: handlePinUnpinChat,
  [SPECTRA_HANDLER_KEYS.DELETE_SPARK]: handleDeleteSpark,
};

// ========================================
// CHAT ACTION HANDLERS
// ========================================

export const handleAddMessage = (data, context) => {
  const { queryClient, chatId, params } = context;
  
  // Skip refetch if this message just finished streaming
  // The streaming process already updated the cache optimistically
  // Only refetch for messages from other users or failed streams
  // NOTE: streaming store is keyed by prompt_id (not answer_id), so use prompt_id for the guard.
  const messageId = data?.prompt_id || data?.answer_id || data?.id || data?.message_id;
  const isTerminalUpdate = isTerminalAnswerStatus(data?.status);
  if (isTerminalUpdate && messageId) {
    useStreamingStateStore.getState().failStreaming(messageId);
  } else if (isStreamingOrSnapshotted(messageId)) {
    // Actively streaming, or a completed snapshot still propagating to Firestore —
    // skip both toast and refetch, we already have the content from streaming.
    return;
  }
  if (messageId && useStreamingStateStore.getState().isUserStopped(messageId)) {
    // User explicitly stopped this generation - skip the Firestore update
    // so the backend's completed answer doesn't overwrite the partial message
    return;
  }
  
  // Reconcile: if a signal is set for this chat, the answer is now persisted —
  // clear it so the spinner doesn't stay stuck when the SSE terminal event was missed.
  useGenerateApiSignal.getState().removeSignal(chatId);

  // Skip toast if user is already viewing this chat (they see messages in real-time)
  if (params?.id === chatId) {
    // Just invalidate cache, no toast needed
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId] });
    return;
  }
  
  // Invalidate cache to refetch messages
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId] });
};

export const handleEditMessage = (data, context) => {
  const { queryClient, chatId, space } = context;
  
  // Update cache directly with edited message
  if (queryClient && chatId && data?.id) {
    queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id], (oldData) => {
      if (!oldData) return oldData;
      
      return {
        ...oldData,
        messages: oldData.messages.map((message) => {
          if (message.id === data.id) {
            return {
              ...message,  // Preserve existing properties
              role: 'user',
              content: data.content,
              answers: data.answers,
              asset_ids: data.asset_ids || [],
              attachments: data.attachments || [],
              expanded: null,
              active: true,
              adk_session_id: data.adk_session_id || null,
            };
          }
          return message;
        })
      };
    });
  }
};

export const handleDeleteMessage = (data, context) => {
  const { queryClient, chatId, space } = context;

  if (!queryClient || !chatId || !data?.prompt_id) return;

  const deletedMessageIds = new Set([
    data.prompt_id,
    data.answer_id,
    ...(data.deleted_prompt_ids || []),
  ].filter(Boolean));
  const streamingStore = useStreamingStateStore.getState();
  deletedMessageIds.forEach((messageId) => streamingStore.clearMessageState(messageId));
  const activeSignal = useGenerateApiSignal.getState().signals?.[chatId];
  if (deletedMessageIds.has(activeSignal?.generate_id)) {
    useGenerateApiSignal.getState().removeSignal(chatId);
  }

  // Version-only delete: remove just the specific answer, keep the message
  if (data?.type === 'MESSAGE:VERSION_DELETE' && data?.answer_id) {
    const deletedPromptIds = new Set(data.deleted_prompt_ids || []);
    queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id], (oldData) => {
      if (!oldData) return oldData;
      const messages = (oldData.messages || oldData || []).filter(
        (message) => !deletedPromptIds.has(message.id)
      ).map((message) => {
        if (message.id !== data.prompt_id) return message;
        const updatedAnswers = (message.answers || []).filter((a) => a.id !== data.answer_id);
        if (updatedAnswers.length === 0) return null;
        return {
          ...message,
          answers: updatedAnswers,
          currentIndex: Math.min(message.currentIndex || 0, updatedAnswers.length - 1),
        };
      }).filter(Boolean);
      return Array.isArray(oldData) ? messages : { ...oldData, messages };
    });
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id] });
    return;
  }

  // Full message delete
  const deletedPromptIds = new Set(data.deleted_prompt_ids || [data.prompt_id]);
  queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id], (oldData) => {
    if (!oldData) return oldData;
    return {
      ...oldData,
      messages: oldData.messages.filter((message) => !deletedPromptIds.has(message.id))
    };
  });
  
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id] });
};

export const handleRegenerateMessage = (data, context) => {
  const { queryClient, chatId } = context;

  // Skip refetch if this answer is still streaming or in the completed-snapshot window.
  // Mirrors the guards in handleAddMessage — regenerate events fire at the same time
  // as completeStreaming(), so without these guards the cache refetch causes a duplicate.
  // NOTE: streaming store is keyed by prompt_id (not answer_id), so use prompt_id for the guard.
  const messageId = data?.prompt_id || data?.answer_id;
  if (isStreamingOrSnapshotted(messageId)) {
    return;
  }

  // Reconcile: answer is persisted — clear signal so spinner doesn't stay stuck
  // when the SSE terminal event was missed (e.g. long media generation).
  useGenerateApiSignal.getState().removeSignal(chatId);

  // Invalidate cache to refetch messages
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId] });
};

export const handleApprovalCreated = (data, context) => {
  const { queryClient, chatId } = context;
  
  if (!chatId || !data?.approval_id) {
    logger.warn('ListenerManager', 'Missing chatId or approval_id for APPROVAL_CREATED event', { data });
    return;
  }
  
  // Invalidate pending approvals query to trigger refetch
  // This will cause the frontend to fetch the latest approvals and render the card
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.PENDING_APPROVALS, chatId] });
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId] });
};

export const handleApprovalUpdated = (data, context) => {
  const { queryClient, chatId } = context;
  // Owner approved, cancelled, or expired the approval. Invalidate pending approvals
  // so collaborators refetch — the approval is no longer pending, so the card
  // disappears. For the approve case this fires before GENERATION_STARTED, giving
  // a clean transition: card disappears → skeleton appears.
  if (!chatId) return;
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.PENDING_APPROVALS, chatId] });
  queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES, chatId] });
};

export const handleSseReconnected = (_data, context) => {
  const { queryClient } = context;

  // SSE reconnected after a drop — refresh all cached message queries.
  // Using a partial key (no chatId) so this works even if context.chatId is
  // stale or null (e.g. user navigated away while connection was down).
  if (queryClient) {
    queryClient.invalidateQueries({ queryKey: [QUERYKEYS.CHAT_MESSAGES] });
    // Re-hydrate live device list in case a device connected/disconnected while
    // the SSE stream was down (G5 — SSE gap coverage).
    queryClient.invalidateQueries({ queryKey: ["desktop-user-devices"] });
  }
};

export const handleGenerationStarted = (data, context) => {
  const { chatId } = context;
  // Generation has genuinely begun on the owner's stream. Collaborators have no
  // SSE token stream of their own — they only see an empty answer doc (ADD_MESSAGE),
  // which otherwise trips isEmptyAwaitingRecovery → a false 30s "Generation
  // interrupted" timeout. Setting the per-chat signal (keyed by chatId, value =
  // prompt message id) makes the FE render the normal generating skeleton and
  // suppresses that false alarm. The existing GENERATION_COMPLETE/FAILED handlers
  // (and SingleMessage's self-heal effect) clear the signal once content arrives.
  const messageId = data?.prompt_id;
  if (!chatId || !messageId) return;
  useGenerateApiSignal.getState().addSignal(chatId, {
    generate_id: data?.turn_id || messageId,
    generation_id: data?.generation_id || null,
    owner_id: data?.owner_id || null,
  });
};

export const handleGenerationComplete = (data, context) => {
  const { queryClient, chatId, space } = context;
  // Generation complete (cross-tab/cross-device realtime event).
  // Clear the local signal so the spinner is released even if the SSE
  // terminal event was missed on the originating tab (e.g. long media job).
  if (chatId) {
    useGenerateApiSignal.getState().removeSignal(chatId);
  }
  // If the event carries final_content, write it directly into the cache so
  // collaborators (and cross-tab viewers) get the answer without a round-trip
  // refetch. Fall through to invalidate regardless to ensure consistency.
  if (queryClient && chatId && data?.final_content && data?.answer_id) {
    queryClient.setQueryData([QUERYKEYS.CHAT_MESSAGES, chatId, space?.space_id], (oldData) => {
      if (!oldData) return oldData;
      return {
        ...oldData,
        messages: oldData.messages.map((message) => {
          const answerIdx = (message.answers || []).findIndex((a) => a.id === data.answer_id);
          if (answerIdx === -1) return message;
          const updatedAnswers = message.answers.map((a) =>
            a.id === data.answer_id
              ? { ...a, content: data.final_content, status: 'complete' }
              : a
          );
          return { ...message, answers: updatedAnswers };
        }),
      };
    });
  }
  if (queryClient && chatId) {
    invalidateChatMessages(queryClient, chatId, space);
  }
  // Release the streaming store (keyed by prompt_id) AFTER the cache writes
  // above, so there is no frame where streaming flips false before the answer
  // is in cache. On the originating tab an early-closed stream (ADK early-ack)
  // left streaming:true so the beaming/activity UI stayed visible; this terminal
  // realtime event is the signal Bug 1's fix defers to — flip it to done now
  // that the answer is persisted, without needing a manual refresh. Idempotent
  // on the normal path (inline SSE GENERATION_COMPLETE already completed it) and
  // a safe no-op for collaborators who never had a local stream.
  if (data?.prompt_id) {
    useStreamingStateStore.getState().completeStreaming(data.prompt_id);
  }
  [data?.prompt_id, data?.answer_id].filter(Boolean).forEach((messageId) => {
    useStreamingStateStore.getState().clearStopping(messageId);
    useStreamingStateStore.getState().clearUserStopped(messageId);
  });
};

export const handleGenerationStopped = (data, context) => {
  const { queryClient, chatId, space } = context;
  // Owner stopped generation. Clear the collaborator's signal (skeleton disappears)
  // and invalidate so they see the partial answer with stopped status.
  if (chatId) {
    useGenerateApiSignal.getState().removeSignal(chatId);
  }
  if (queryClient && chatId) {
    invalidateChatMessages(queryClient, chatId, space);
  }
  // Release the streaming store so a stuck streaming:true (from an early-closed
  // stream) doesn't keep the frozen beaming/activity UI visible. failStreaming
  // keeps the completed-tools/activity snapshot with streaming:false.
  if (data?.prompt_id) {
    useStreamingStateStore.getState().failStreaming(data.prompt_id);
  }
  [data?.prompt_id, data?.answer_id].filter(Boolean).forEach((messageId) => {
    useStreamingStateStore.getState().clearStopping(messageId);
  });
};

export const handleGenerationFailed = (data, context) => {
  const { queryClient, chatId, space } = context;
  // Another device/tab has a failed generation. Clear the collaborator's signal
  // (mirrors handleGenerationComplete/handleGenerationStopped) so the skeleton
  // doesn't stay stuck forever on a failure — without this, self-heal in
  // SingleMessage never fires because a failed generation usually has no
  // content to satisfy isAnswerAvailable. Also invalidate messages so the
  // failure status is reflected immediately on this tab.
  if (chatId) {
    useGenerateApiSignal.getState().removeSignal(chatId);
  }
  if (queryClient && chatId) {
    invalidateChatMessages(queryClient, chatId, space);
  }
  // Release the streaming store so a stuck streaming:true (from an early-closed
  // stream) doesn't keep the frozen beaming/activity UI visible on failure.
  if (data?.prompt_id) {
    useStreamingStateStore.getState().failStreaming(data.prompt_id);
  }
  [data?.prompt_id, data?.answer_id].filter(Boolean).forEach((messageId) => {
    useStreamingStateStore.getState().clearStopping(messageId);
    useStreamingStateStore.getState().clearUserStopped(messageId);
  });
};

export const handleArtifactUpdated = (data, context) => {
  const { queryClient, chatId, space, notification, params } = context;
  // An inline AI edit or manual save just created a new artifact version.
  // Invalidate CHAT_MESSAGES so the Nebula canvas content refreshes for
  // collaborators, and ARTIFACT_VERSIONS so the version history panel updates.
  // Also clear the editing signal so the "Editing…" indicator is dismissed.
  if (data?.answer_id) {
    useArtifactEditSignal.getState().clearEditing(data.answer_id);
  }
  // Notify the collaborator only when they're not currently viewing this chat —
  // if they're already on the canvas they'll see the live update directly.
  if (notification && params?.id !== chatId) {
    notification.addNotification({
      category: 'space',
      message: 'Artifact updated',
      navigateTo: space?.space_id
        ? `/spectra/${space.space_id}/chat/${chatId}`
        : `/chat/${chatId}`,
    });
  }
  if (queryClient && chatId) {
    invalidateChatMessages(queryClient, chatId, space);
    if (data?.answer_id) {
      queryClient.invalidateQueries({
        queryKey: [QUERYKEYS.ARTIFACT_VERSIONS, chatId, data.answer_id],
      });
    }
  }
};

export const handleArtifactEditStarted = (data, context) => {
  // The space owner started an inline AI edit. Show an "Editing…" indicator
  // on collaborators' Nebula canvas until ARTIFACT_UPDATED clears it.
  if (data?.answer_id) {
    useArtifactEditSignal.getState().setEditing(data.answer_id);
  }
};

// ========================================
// CHAT HANDLER REGISTRY
// ========================================

export const chatHandlers = {
  [CHAT_ACTIONS.ADD_MESSAGE]: handleAddMessage,
  [CHAT_ACTIONS.EDIT_MESSAGE]: handleEditMessage,
  [CHAT_ACTIONS.DELETE_MESSAGE]: handleDeleteMessage,
  [CHAT_ACTIONS.REGENERATE_MESSAGE]: handleRegenerateMessage,
  [CHAT_ACTIONS.APPROVAL_CREATED]: handleApprovalCreated,
  [CHAT_ACTIONS.APPROVAL_UPDATED]: handleApprovalUpdated,
  [CHAT_ACTIONS.SSE_RECONNECTED]: handleSseReconnected,
  [CHAT_ACTIONS.GENERATION_STARTED]: handleGenerationStarted,
  [CHAT_ACTIONS.GENERATION_COMPLETE]: handleGenerationComplete,
  [CHAT_ACTIONS.GENERATION_STOPPED]: handleGenerationStopped,
  [CHAT_ACTIONS.GENERATION_FAILED]: handleGenerationFailed,
  [CHAT_ACTIONS.ARTIFACT_UPDATED]: handleArtifactUpdated,
  [CHAT_ACTIONS.ARTIFACT_EDIT_STARTED]: handleArtifactEditStarted,
};

// Derived from chatHandlers so subscribeChat/unsubscribeChat always stay in
// sync with the handler registry — adding a handler automatically subscribes it.
export const CHAT_CORE_ACTIONS = Object.keys(chatHandlers);
